/**
 * The goal analysis, as a pure function.
 *
 * Same reasoning as `goalReadout.ts` next door: the interesting part is the
 * branching, and a sentence built inside JSX can only be checked by signing
 * in and arranging exactly the right state by hand. Here that matters more
 * than usual, because several of these sentences are about somebody's body
 * and the difference between a fair one and an overbearing one is a clause.
 *
 * ── The rules these sentences are held to ─────────────────────────────────
 *
 * `weightPlan.ts` states them for the maths; they bind the wording too, and
 * this is where they are easiest to break:
 *
 *   1. Never phrase a figure as an instruction. "Holding this pace works out
 *      at around 2,020 kcal a day" is a calculation. "Eat 2,020 kcal" is
 *      advice, and this app is not qualified to give it. Every energy
 *      sentence here is built around "works out at" / "would require", never
 *      an imperative.
 *   2. Never call a pace safe or unsafe FOR THIS READER. It is inside or
 *      outside a commonly cited range. The app knows the range; it does not
 *      know them.
 *   3. Never print a figure that fell through the floor. When
 *      `belowIntakeFloor` is set, `impliedIntakeKcal` is null by design and
 *      the sentence says the goal is not reachable at a gradual pace — it
 *      does not quote the number and caption it.
 *
 * And one that is specific to encouragement: a goal being on course is worth
 * saying out loud, because a page that only ever speaks up to warn you
 * teaches you to dread opening it. But it is said as a fact about the
 * numbers, never as praise for the person.
 */
import { KCAL_PER_KG, MODERATE_DEFICIT_KCAL } from '@loggy/shared';

import { asWeightDelta, asWeightDeltaRange } from '../charts/weightPlanChartMath.js';
import type { PlanAssessment } from '@loggy/shared';

import { formatDeadline } from './goalReadout.js';

export type PlanTone = 'achieved' | 'on-course' | 'watch' | 'over';

/**
 * One figure, with its own label and the line of context that stops it being
 * a bare number.
 *
 * This replaced four paragraphs of sentences with numbers embedded in them.
 * The owner's complaint was that there was "a lot of text with numbers" under
 * the charts, and the thing they actually wanted — how much they can eat —
 * was a clause in the middle of the third paragraph. A figure that has to be
 * read to be found is not presented, it is merely present.
 */
export interface PlanStat {
  label: string;
  /** Already formatted. The caller prints it, never computes from it. */
  value: string;
  /** The unit, kept separate so it can be set smaller beside the figure. */
  unit: string | null;
  /** One short line under it, or nothing. */
  detail: string | null;
  /** The one figure the reader came for, given the room to say so. */
  lead?: boolean;
  /**
   * How this particular figure is doing, as a quiet border and wash rather
   * than a badge. Absent is neutral: most figures are just figures.
   *
   * Colour is never the only carrier. Every toned tile also says why in its
   * detail line — "A moderate deficit is 574–750" beside 1,848 is the
   * message; the amber only makes you look. A reader who cannot see the
   * difference loses the glance, not the meaning.
   */
  tone?: PlanStatTone;
}

/**
 * `good` on or near what was asked, `watch` at the edge of it, `over` past
 * the point where a plan should go.
 *
 * These use the reserved status colours, which is correct here and would not
 * be on a chart: the design system reserves them FOR status, and forbids a
 * chart series borrowing them. A tile saying how a goal stands is exactly
 * the case they exist for.
 */
export type PlanStatTone = 'good' | 'watch' | 'over';

export interface PlanReadout {
  tone: PlanTone;
  /** One sentence, the whole verdict. Always present. */
  headline: string;
  /** The dashboard: the figures, in the order they are worth reading. */
  stats: PlanStat[];
  /**
   * What to do about it — options to scan, never a paragraph to read.
   *
   * This was three sentences of prose, which is exactly what the owner asked
   * to be rid of. Somebody looking at a goal that has slipped wants to know
   * their choices, and choices are a list.
   */
  suggestion: { title: string; options: string[] } | null;
  /** Shown whenever any energy figure appears above. */
  caveat: string | null;
}

/** "2,020" — thousands separated, because a four-digit calorie figure read
 *  as "2020" looks like a year on a page that is full of dates. */
const kcalFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

function kcal(value: number): string {
  return kcalFormatter.format(Math.round(value));
}

function rate(value: number, unit: string): string {
  return `${value.toFixed(2)} ${unit}/week`;
}

/**
 * A pace, with the sign a scale would have shown: down negative, up positive.
 *
 * The maths carries paces as movement TOWARDS the target, and the chart
 * already flips them for display. The tiles did not, so the same week showed
 * as −0.80 on one and +0.66 on the other. `asWeightDelta` is the chart's own
 * function, imported rather than reimplemented — two flips written twice are
 * two flips that will disagree.
 */
function signedRate(value: number, goingDown: boolean, precision = 2): string {
  const delta = asWeightDelta(value, goingDown);
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${Math.abs(delta).toFixed(precision)}`;
}

/** A band, flipped the same way. Negating turns it inside out: the fastest
 *  pace is the biggest magnitude and so the LOWEST number. */
function signedBand(
  band: { min: number; max: number },
  goingDown: boolean,
): string {
  const flipped = asWeightDeltaRange(band, goingDown)!;
  return `${signedRate(asWeightDelta(flipped.min, goingDown), goingDown)} to ${signedRate(asWeightDelta(flipped.max, goingDown), goingDown)}`;
}

function amount(value: number, unit: string, precision: number): string {
  return `${Math.abs(value).toFixed(precision)} ${unit}`;
}

/**
 * How far apart two dates are, in whole weeks — used only to say how much
 * drifting costs, so a rounded figure is the honest precision. "About three
 * weeks" is what a reader can act on; "23.4 days" implies the model is
 * sharper than it is.
 */
function weeksBetween(a: string, b: string): number {
  const from = Date.parse(a);
  const to = Date.parse(b);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / (7 * 24 * 60 * 60 * 1000));
}

/**
 * The two levers, as lines rather than a paragraph.
 *
 * Move the deadline, or move the target. Both are always true and neither is
 * recommended over the other — which of your own goals to bend is not this
 * app's call. The second option carries the weight a gradual pace WOULD
 * reach by the date already set, because "move your deadline" on its own
 * leaves somebody with one option they may not want.
 */
function options(
  a: PlanAssessment,
  unit: string,
  precision: number,
  targetDate: string | null,
): string[] {
  const out: string[] = [];
  if (a.healthyDeadline) {
    out.push(`Move the deadline to around ${formatDeadline(a.healthyDeadline.date)}`);
  } else {
    out.push('Move the deadline out');
  }
  if (a.achievableByDeadline !== null && targetDate !== null) {
    out.push(
      `Or aim for about ${a.achievableByDeadline.toFixed(precision)} ${unit} by ${formatDeadline(targetDate)}`,
    );
  } else {
    out.push('Or aim for a smaller change');
  }
  return out;
}

/**
 * The moderate energy band, in kcal a day, derived from the PACE band.
 *
 * One function because this figure appears on more than one tile, and the
 * last time it did not it went wrong in public: a deficit constant was
 * printed beside a surplus and contradicted the tile below it. Anything that
 * says "a gradual pace" has to mean the same thing by it.
 *
 * Null without a profile, where there is no personal expenditure to convert
 * a pace into calories with.
 */
function healthyEnergyBand(a: PlanAssessment): { min: number; max: number } | null {
  if (!a.healthyRange) return null;
  return {
    min: (a.healthyRange.min * KCAL_PER_KG) / 7,
    max: (a.healthyRange.max * KCAL_PER_KG) / 7,
  };
}

/**
 * What "a gradual pace" actually asks of somebody, in the terms they can act
 * on — calories where there is a profile, kilograms a week where there is
 * not. The phrase is meaningless on its own, and it is on several tiles.
 */
function healthyMeans(a: PlanAssessment, unit: string, noun: string): string {
  const band = healthyEnergyBand(a);
  if (band) return `With a ${noun} of about ${kcal(band.min)}–${kcal(band.max)} kcal a day`;
  return `About ${a.gradualRange.min.toFixed(2)}–${a.gradualRange.max.toFixed(2)} ${unit} a week`;
}

/**
 * How the energy figures are doing, against the band that describes them.
 *
 * One rule for both the intake tile and the deficit tile, because they are
 * two views of the same number and toning them separately would let them
 * disagree on the same screen.
 */
function energyTone(a: PlanAssessment): PlanStatTone | undefined {
  if (!a.energy) return undefined;
  // Past the floor, or past what the body uses at all. Not an edge case —
  // a plan that cannot be run.
  if (a.energy.belowIntakeFloor || a.energy.impliedIntakeKcal === null) return 'over';

  const gap = a.energy.requiredDailyGapKcal;
  if (gap === null) return undefined;
  const band = healthyEnergyBand(a) ?? MODERATE_DEFICIT_KCAL;
  const magnitude = Math.abs(gap);

  if (magnitude <= band.max) return 'good';
  // Between the moderate band and the floor: still runnable, and the place
  // where saying something early is worth more than saying it loudly later.
  return 'watch';
}

/** The pace, against the same band in its own unit. */
function paceTone(a: PlanAssessment): PlanStatTone | undefined {
  if (a.requiredRateKgPerWeek === null) return undefined;
  if (a.verdict === 'not-gradually-reachable') return 'over';
  const band = a.healthyRange ?? a.gradualRange;
  return a.requiredRateKgPerWeek <= band.max ? 'good' : 'watch';
}

export function buildPlanReadout(input: {
  assessment: PlanAssessment;
  unit: string;
  precision: number;
  /** The deadline the reader set for themselves. */
  targetDate: string | null;
  /** Losing or gaining, in the reader's own words rather than the app's
   *  opinion — taken from their baseline and target, never from the catalog. */
  goingDown: boolean;
}): PlanReadout {
  const { assessment: a, unit, precision, targetDate, goingDown } = input;
  const verb = goingDown ? 'lose' : 'gain';
  const noun = goingDown ? 'deficit' : 'surplus';
  const stats: PlanStat[] = [];

  /**
   * The lead figure: what this goal comes to in calories a day.
   *
   * Phrased as a calculation and never an instruction — "your goal comes to
   * ~2,020 kcal a day" is arithmetic, "eat 2,020 kcal" is advice this app is
   * not qualified to give. The label carries that distinction, which is why
   * it is not simply "Eat".
   */
  if (a.energy) {
    if (a.energy.impliedIntakeKcal !== null) {
      stats.push({
        label: 'Your goal comes to',
        value: kcal(a.energy.impliedIntakeKcal),
        unit: 'kcal a day',
        /**
         * A cautioned tile has to SAY why. The first version of this
         * dashboard turned it amber and left the reason behind in the prose
         * it replaced — a colour is a signal, not an explanation, and
         * somebody colour-blind would have had neither.
         */
        detail: a.energy.belowIntakeFloor
          ? 'Below the level generally considered to need medical supervision — worth taking to a doctor before going near it.'
          : `Your body uses about ${kcal(a.energy.tdeeKcal)} a day`,
        lead: true,
        // Real arithmetic that lands somewhere a plan should not go. Shown,
        // because withholding a calculation about somebody's own goal is the
        // app deciding for them — but never shown quietly.
        tone: energyTone(a),
      });
    } else {
      stats.push({
        label: 'Your goal comes to',
        value: 'More than you use',
        unit: null,
        detail: `Reaching it by that date would need a ${noun} bigger than everything your body uses in a day.`,
        lead: true,
        tone: 'over',
      });
    }
  }

  /**
   * The number the reader STEERS by.
   *
   * It was a clause in the lead tile's detail line, and the owner is right
   * that this is not enough: "anders heeft de gebruiker niets om te sturen."
   * An intake figure tells you where to end up; a deficit tells you what to
   * change, and it is the one that survives a day of eating out or a week on
   * holiday — you know what you are aiming to be short by.
   *
   * The moderate range sits underneath so the figure means something. 725 is
   * just a number; 725 against a commonly cited 500–750 says you are at the
   * top of the ordinary range, and 1,848 against it says how far outside it
   * you are.
   */
  if (a.energy && a.energy.requiredDailyGapKcal !== null) {
    const gap = Math.abs(a.energy.requiredDailyGapKcal);
    /**
     * The moderate band, converted from the PACE band rather than quoted
     * from a constant of its own.
     *
     * The first version printed the deficit constant, 500–750, for a gain
     * goal too — where it is the wrong figure twice over. It contradicted
     * the tile right below it: a 433 kcal surplus read as inside "500–750"
     * while the pace it produces, 0.39 kg a week, read as outside
     * "0.15–0.31". Two references for one thing, disagreeing in public.
     * Derived from `healthyRange`, they cannot.
     */
    const band = healthyEnergyBand(a) ?? MODERATE_DEFICIT_KCAL;
    stats.push({
      label: `Daily ${noun}`,
      value: kcal(gap),
      unit: 'kcal a day',
      detail: `A healthy ${noun} is ${kcal(band.min)}–${kcal(band.max)}`,
      tone: energyTone(a),
    });
  }

  if (a.requiredRateKgPerWeek !== null) {
    stats.push({
      label: 'Required pace',
      value: signedRate(a.requiredRateKgPerWeek, goingDown),
      unit: `${unit} a week`,
      detail: a.healthyRange
        ? `A healthy ${noun} gives ${signedBand(a.healthyRange, goingDown)}`
        : `Commonly cited healthy range ${signedBand(a.gradualRange, goingDown)}`,
      tone: paceTone(a),
    });
  }

  if (a.recentRateKgPerWeek !== null) {
    stats.push({
      label: 'Your recent pace',
      value: signedRate(a.recentRateKgPerWeek, goingDown),
      unit: `${unit} a week`,
      detail:
        a.aheadByKg === null
          ? null
          : a.aheadByKg > 0
            ? `About ${amount(a.aheadByKg, unit, precision)} ahead of the plan`
            : a.aheadByKg < 0
              ? `About ${amount(a.aheadByKg, unit, precision)} behind the plan`
              : 'Level with the plan',
      /* Ahead or level is good. Behind is only worth a colour once it is
         behind by more than a week's own target — every plan wobbles by less
         than that, and a tile that turns amber over a single heavy dinner
         teaches the reader to ignore it. */
      tone:
        a.aheadByKg === null
          ? undefined
          : a.aheadByKg >= 0
            ? 'good'
            : a.requiredRateKgPerWeek !== null &&
                Math.abs(a.aheadByKg) > Math.abs(a.requiredRateKgPerWeek)
              ? 'watch'
              : undefined,
    });
  }

  if (a.remainingKg !== null) {
    /**
     * Past the target is not "0.6 kg left to lose".
     *
     * Found by running the near-the-target case the owner asked for: at
     * 104.4 kg against a 105 kg goal the tile read "Left to lose 0.6 kg",
     * which is the opposite of what happened. The distance is the same
     * number; what it means is not.
     */
    const passed = a.remainingKg <= 0;
    stats.push({
      label: passed ? 'Past your target' : `Left to ${verb}`,
      value: Math.abs(a.remainingKg).toFixed(precision),
      unit,
      detail: passed
        ? 'You are through it'
        : targetDate
          ? `By ${formatDeadline(targetDate)}`
          : 'No deadline set',
      /* "On or close to the target" — within one week's required pace of it,
         which is the point at which it stops being a distance and starts
         being an arrival. */
      tone:
        a.remainingKg <= 0
          ? 'good'
          : a.requiredRateKgPerWeek !== null && a.remainingKg <= Math.abs(a.requiredRateKgPerWeek)
            ? 'good'
            : undefined,
    });
  }

  /**
   * The arrival tile is always the HEALTHY pace, never the reader's own.
   *
   * It briefly showed "At your pace, there by" whenever a recent pace could
   * be worked out, which the owner had not asked for and did not want: this
   * tile answers when a sensible plan gets there, and their own pace already
   * has a tile of its own two rows up. Two tiles reporting the same journey
   * by different rules is a dashboard arguing with itself.
   */
  if (a.healthyDeadline) {
    stats.push({
      label: 'A healthy pace reaches it by',
      value: formatDeadline(a.healthyDeadline.date),
      unit: null,
      detail: healthyMeans(a, unit, noun),
      tone:
        targetDate === null
          ? undefined
          : Date.parse(a.healthyDeadline.date) <= Date.parse(targetDate)
            ? 'good'
            : 'watch',
    });
  }

  // ---- The verdict itself ------------------------------------------------
  let tone: PlanTone;
  let headline: string;
  let suggestion: PlanReadout['suggestion'] = null;

  switch (a.verdict) {
    case 'achieved':
      tone = 'achieved';
      headline = 'You have reached this goal.';
      break;

    case 'open-ended':
      tone = 'on-course';
      headline = 'This goal has no deadline, so there is no pace it has to keep.';
      break;

    case 'unknown':
      tone = 'on-course';
      headline = 'Not enough readings yet to say how this is going.';
      break;

    case 'comfortable':
      tone = 'on-course';
      headline = 'Your goal asks for less than the usual healthy pace — there is room in this plan.';
      break;

    case 'ambitious':
      tone = 'on-course';
      // Encouragement, as a fact about the numbers rather than praise.
      headline = 'Your goal is ambitious, and it sits inside the range usually cited for healthy change.';
      // Nothing to suggest, and nothing to repeat: "0.3 kg ahead of the plan"
      // is already the detail line under "Your recent pace". Saying it again
      // in a sentence is the prose this dashboard replaced.
      break;

    case 'beyond-gradual':
      // The EARLY warning. Reached while there is still something to do.
      tone = 'watch';
      headline = `This deadline now asks for more than the range usually cited for healthy change — still reachable, but it is moving away from it.`;
      suggestion = { title: 'To bring it back inside that range', options: options(a, unit, precision, targetDate) };
      break;

    case 'not-gradually-reachable':
      tone = 'over';
      headline = 'This goal is no longer reachable by its deadline at a healthy pace.';
      suggestion = { title: 'To put it back within reach', options: options(a, unit, precision, targetDate) };
      break;

    default:
      tone = 'on-course';
      headline = 'Not enough readings yet to say how this is going.';
  }

  const caveat =
    a.energy !== null
      ? 'Mifflin–St Jeor estimate: a population average, so an individual can sit ~10% either side. Updates with every measurement.'
      : null;

  return { tone, headline, stats, suggestion, caveat };
}
